import { useTranslation } from 'react-i18next'
/**
 * Referral Fee Info Component - v2.8
 * Shows referral fee deductions for users who came via referral link
 * Displayed on Dashboard/Portfolio for referred users
 *
 * @description Uses config from API to display CASCADE v3.0 referral rates
 * @see ConfigContext.tsx - provides useConfig() hook
 * @see config.controller.ts - backend API endpoint
 */

import { Info, AlertTriangle } from 'lucide-react'
import { useConfig } from '../../contexts/ConfigContext'

interface ReferralFeeInfoProps {
  referralLevel?: number // 1, 2, 3 or undefined if no referrer
  totalPendingUSDT?: number
  totalPendingTAKARA?: number
}

// Total fee for each level (cumulative - L1 also pays to L2 and L3 above)
const getTotalReferralFee = (
  level: number,
  rates: Record<number, number>
): number => {
  let total = 0
  for (let l = 1; l <= level; l++) {
    if (rates[l]) total += rates[l]
  }
  return total
}

export default function ReferralFeeInfo({
  referralLevel,
  totalPendingUSDT = 0,
  totalPendingTAKARA = 0
}: ReferralFeeInfoProps) {
  const { t } = useTranslation(['referral', 'common'])
  const config = useConfig()

  // Don't show if user has no referrer
  if (!referralLevel || referralLevel < 1) {
    return null
  }

  // Get rates from API config (CASCADE v3.0)
  const usdtReferralRates = config.referral.usdtRates
  const takaraReferralRates = config.referral.takaraRates
  const treasuryTaxPercent = config.referral.treasuryTaxPercent

  const usdtRate = usdtReferralRates[referralLevel] || 0
  const takaraRate = takaraReferralRates[referralLevel] || 0

  // Calculate total fees
  const totalUsdtFee = getTotalReferralFee(referralLevel, usdtReferralRates)
  const totalTakaraFee = getTotalReferralFee(referralLevel, takaraReferralRates)

  // Calculate estimated deductions from pending rewards
  const estimatedUsdtDeduction = totalPendingUSDT * totalUsdtFee
  const estimatedTakaraDeduction = totalPendingTAKARA * totalTakaraFee

  const formatPercent = (rate: number) => (rate * 100).toFixed(1) + '%'

  return (
    <div className="bg-background-card rounded-xl border border-blue-900/30 p-4 mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Info className="h-5 w-5 text-blue-400" />
        <h4 className="text-sm font-semibold text-blue-400">Referral Fee Information</h4>
        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded ml-auto">
          Level {referralLevel}
        </span>
      </div>

      {/* Explanation */}
      <p className="text-xs text-gray-400 mb-3">
        As you joined via a referral link, a portion of your claim rewards goes to your referrer chain.
      </p>

      {/* Fee Rates */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {/* USDT Fee */}
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">USDT Claims Fee</div>
          <div className="text-lg font-bold text-green-400">{formatPercent(totalUsdtFee)}</div>
          <div className="text-xs text-gray-500">of your USDT rewards</div>
        </div>

        {/* TAKARA Fee */}
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">TAKARA Claims Fee</div>
          <div className="text-lg font-bold text-gold-400">{formatPercent(totalTakaraFee)}</div>
          <div className="text-xs text-gray-500">+ {treasuryTaxPercent}% treasury tax</div>
        </div>
      </div>

      {/* Pending Deductions Preview */}
      {(totalPendingUSDT > 0 || totalPendingTAKARA > 0) && (
        <div className="bg-yellow-900/10 border border-yellow-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span className="text-xs font-medium text-yellow-400">Estimated Deductions from Pending</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {totalPendingUSDT > 0 && (
              <div>
                <span className="text-gray-400">USDT: </span>
                <span className="text-yellow-400">-${estimatedUsdtDeduction.toFixed(2)}</span>
                <span className="text-gray-500"> of ${totalPendingUSDT.toFixed(2)}</span>
              </div>
            )}
            {totalPendingTAKARA > 0 && (
              <div>
                <span className="text-gray-400">TAKARA: </span>
                <span className="text-yellow-400">-{estimatedTakaraDeduction.toFixed(2)}</span>
                <span className="text-gray-500"> of {totalPendingTAKARA.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer Note */}
      <p className="text-xs text-gray-500 mt-3 text-center">
        This fee supports the referral network that brought you to the platform
      </p>
    </div>
  )
}
